import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, downloadBlobResponse } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Download, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import StickyHeader from "@/components/StickyHeader";

const KATEGORI_LABEL = { setara: "Setara", junior: "Junior", senior: "Senior" };
const EMPTY_PERIOD_FORM = { nama: "", tanggal_mulai: "", tanggal_selesai: "", prodi_ids_aktif: [] };

export default function PeerAssessmentAdmin() {
    const [periods, setPeriods] = useState([]);
    const [prodiList, setProdiList] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState("");

    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_PERIOD_FORM);
    const [confirmDel, setConfirmDel] = useState(null);

    const [compliance, setCompliance] = useState([]);
    const [results, setResults] = useState([]);
    const [loadingTab, setLoadingTab] = useState(false);

    const prodiMap = useMemo(() => {
        const m = {};
        prodiList.forEach((p) => (m[p.id] = p.nama_prodi));
        return m;
    }, [prodiList]);

    const loadPeriods = async () => {
        try {
            const { data } = await api.get("/peer-assessment/periods");
            setPeriods(data);
            if (!selectedPeriodId && data.length > 0) setSelectedPeriodId(data[0].id);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    useEffect(() => {
        loadPeriods();
        api.get("/prodi").then(({ data }) => setProdiList(data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedPeriodId) return;
        setLoadingTab(true);
        Promise.all([
            api.get("/peer-assessment/compliance", { params: { period_id: selectedPeriodId } }),
            api.get("/peer-assessment/results", { params: { period_id: selectedPeriodId } }),
        ])
            .then(([c, r]) => { setCompliance(c.data); setResults(r.data); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
            .finally(() => setLoadingTab(false));
    }, [selectedPeriodId]);

    const openCreate = () => { setEditing(null); setForm(EMPTY_PERIOD_FORM); setDialog(true); };
    const openEdit = (p) => {
        setEditing(p);
        setForm({ nama: p.nama, tanggal_mulai: p.tanggal_mulai, tanggal_selesai: p.tanggal_selesai, prodi_ids_aktif: p.prodi_ids_aktif || [] });
        setDialog(true);
    };
    const toggleProdi = (id) => {
        setForm((f) => ({
            ...f,
            prodi_ids_aktif: f.prodi_ids_aktif.includes(id)
                ? f.prodi_ids_aktif.filter((x) => x !== id)
                : [...f.prodi_ids_aktif, id],
        }));
    };
    // "Pilih Semua" murni bantuan UI (centang/kosongkan checkbox satu per satu) --
    // TIDAK mengubah makna data tersimpan: array kosong tetap berarti "semua prodi"
    // di backend, terlepas dari apakah semua id eksplisit tercentang di sini atau
    // form baru dibuka kosong. allProdiSelected pakai prodiList.length agar re-render
    // otomatis ikut benar kalau daftar prodi berubah.
    const allProdiSelected = prodiList.length > 0 && form.prodi_ids_aktif.length === prodiList.length;
    const someProdiSelected = form.prodi_ids_aktif.length > 0 && !allProdiSelected;
    const togglePilihSemua = () => {
        setForm((f) => ({ ...f, prodi_ids_aktif: allProdiSelected ? [] : prodiList.map((p) => p.id) }));
    };

    const submitPeriod = async () => {
        try {
            if (editing) await api.put(`/peer-assessment/periods/${editing.id}`, form);
            else await api.post("/peer-assessment/periods", form);
            toast.success(editing ? "Periode diperbarui" : "Periode ditambahkan");
            setDialog(false);
            loadPeriods();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const toggleStatus = async (p) => {
        try {
            await api.put(`/peer-assessment/periods/${p.id}`, { status: p.status === "aktif" ? "nonaktif" : "aktif" });
            toast.success("Status periode diperbarui");
            loadPeriods();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const delPeriod = async () => {
        try {
            await api.delete(`/peer-assessment/periods/${confirmDel.id}`);
            toast.success("Periode dihapus");
            setConfirmDel(null);
            loadPeriods();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const exportExcel = async () => {
        try {
            const res = await api.get("/peer-assessment/results/export", {
                params: { period_id: selectedPeriodId }, responseType: "blob",
            });
            downloadBlobResponse(res, "aura_peer-assessment.xlsx");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Peer Assessment</div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Plus Jakarta Sans" }}>
                            <HeartHandshake className="w-7 h-7 text-teal-600" /> Peer Assessment Budaya CIPTO
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Kelola & monitoring penilaian antar sejawat PPDS. Penilai bersifat anonim — hasil skor tidak
                            pernah bisa ditelusuri balik ke siapa penilainya, termasuk oleh Super Admin.
                        </p>
                    </div>
                </div>
            </StickyHeader>

            <Tabs defaultValue="periode">
                <TabsList>
                    <TabsTrigger value="periode" data-testid="peer-tab-periode">Periode</TabsTrigger>
                    <TabsTrigger value="kepatuhan" data-testid="peer-tab-kepatuhan">Kepatuhan Pengisian</TabsTrigger>
                    <TabsTrigger value="rekap" data-testid="peer-tab-rekap">Rekap Hasil</TabsTrigger>
                </TabsList>

                <TabsContent value="periode">
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="p-4 border-b flex justify-end">
                            <Button data-testid="peer-period-add-button" onClick={openCreate} className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                                <Plus className="w-4 h-4 mr-2" /> Tambah Periode
                            </Button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full compact">
                                <thead className="bg-slate-100">
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                        <th>Nama Periode</th><th>Tanggal Mulai</th><th>Tanggal Selesai</th>
                                        <th>Departemen Aktif</th><th>Status</th><th className="text-right pr-4">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {periods.length === 0 ? (
                                        <tr><td colSpan={6} className="text-center py-8 text-slate-400">Belum ada periode.</td></tr>
                                    ) : periods.map((p) => (
                                        <tr key={p.id} className="border-t border-slate-200 hover:bg-slate-50">
                                            <td className="font-semibold text-slate-800">{p.nama}</td>
                                            <td className="text-xs">{p.tanggal_mulai}</td>
                                            <td className="text-xs">{p.tanggal_selesai}</td>
                                            <td className="text-xs text-slate-500">
                                                {p.prodi_ids_aktif?.length ? `${p.prodi_ids_aktif.length} prodi` : "Semua prodi"}
                                            </td>
                                            <td>
                                                <button onClick={() => toggleStatus(p)}>
                                                    <Badge variant={p.status === "aktif" ? "default" : "outline"} className={p.status === "aktif" ? "bg-emerald-600" : ""}>
                                                        {p.status === "aktif" ? "Aktif" : "Nonaktif"}
                                                    </Badge>
                                                </button>
                                            </td>
                                            <td className="text-right pr-2">
                                                <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                                                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmDel(p)}><Trash2 className="w-4 h-4" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="kepatuhan">
                    <PeriodPicker periods={periods} value={selectedPeriodId} onChange={setSelectedPeriodId} />
                    <Card className="border-slate-200 shadow-sm overflow-hidden mt-3">
                        <div className="overflow-x-auto">
                            <table className="w-full compact">
                                <thead className="bg-slate-100">
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                        <th>Nama</th><th>NPM</th><th>Prodi</th><th>Progress</th><th>Kategori Selesai</th><th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingTab ? (
                                        <tr><td colSpan={6} className="text-center py-8 text-slate-400">Memuat...</td></tr>
                                    ) : compliance.length === 0 ? (
                                        <tr><td colSpan={6} className="text-center py-8 text-slate-400">Tidak ada data untuk periode ini.</td></tr>
                                    ) : compliance.map((r) => (
                                        <tr key={r.npm} className="border-t border-slate-200 hover:bg-slate-50">
                                            <td className="font-semibold text-slate-800">{r.nama}</td>
                                            <td className="font-mono text-xs">{r.npm}</td>
                                            <td className="text-slate-600 text-xs">{r.prodi_nama}</td>
                                            <td className="font-semibold">{r.jumlah_selesai}/{r.target}</td>
                                            <td className="flex gap-1 flex-wrap py-2">
                                                {["setara", "junior", "senior"].map((k) => (
                                                    <Badge key={k} variant="outline" className={r.kategori_selesai.includes(k) ? "border-emerald-400 text-emerald-700" : "text-slate-400"}>
                                                        {KATEGORI_LABEL[k]}
                                                    </Badge>
                                                ))}
                                            </td>
                                            <td>
                                                <Badge className={r.lengkap ? "bg-emerald-600" : "bg-amber-500"}>
                                                    {r.lengkap ? "Lengkap" : "Belum lengkap"}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500">
                            Tabel ini TIDAK menampilkan skor apa pun — hanya status kepatuhan pengisian.
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="rekap">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <PeriodPicker periods={periods} value={selectedPeriodId} onChange={setSelectedPeriodId} />
                        <Button variant="outline" onClick={exportExcel} disabled={!selectedPeriodId || results.length === 0}>
                            <Download className="w-4 h-4 mr-2" /> Export Excel
                        </Button>
                    </div>
                    <div className="space-y-3 mt-3">
                        {loadingTab ? (
                            <div className="text-center py-8 text-slate-400">Memuat...</div>
                        ) : results.length === 0 ? (
                            <div className="text-center py-8 text-slate-400">Belum ada hasil penilaian untuk periode ini.</div>
                        ) : results.map((r) => (
                            <Card key={r.target_ppds_id} className="border-slate-200 shadow-sm">
                                <CardContent className="p-5 space-y-3">
                                    <div className="flex items-start justify-between flex-wrap gap-2">
                                        <div>
                                            <div className="font-bold text-slate-800">{r.nama}</div>
                                            <div className="text-xs text-slate-500">{r.npm} · {r.prodi_nama} · {r.n_penilai} penilai</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-extrabold text-teal-700">{r.avg_total ?? "-"}</div>
                                            <Badge className={
                                                r.kategori_label === "Sangat Baik" ? "bg-emerald-600"
                                                : r.kategori_label === "Baik" ? "bg-teal-600"
                                                : r.kategori_label === "Cukup" ? "bg-amber-500" : "bg-rose-600"
                                            }>{r.kategori_label}</Badge>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                                        {Object.entries(r.avg_per_kategori).map(([cat, avg]) => (
                                            <div key={cat} className="bg-slate-50 rounded-lg p-2 text-center">
                                                <div className="text-slate-500">{cat}</div>
                                                <div className="font-bold text-slate-800">{avg ?? "-"}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {(r.catatan.kekuatan.length || r.catatan.area_pengembangan.length || r.catatan.saran.length) > 0 && (
                                        <div className="space-y-2 text-xs border-t pt-3">
                                            {r.catatan.kekuatan.length > 0 && (
                                                <div><span className="font-semibold text-slate-600">Kekuatan menonjol: </span>{r.catatan.kekuatan.join(" · ")}</div>
                                            )}
                                            {r.catatan.area_pengembangan.length > 0 && (
                                                <div><span className="font-semibold text-slate-600">Area pengembangan: </span>{r.catatan.area_pengembangan.join(" · ")}</div>
                                            )}
                                            {r.catatan.saran.length > 0 && (
                                                <div><span className="font-semibold text-slate-600">Saran perbaikan: </span>{r.catatan.saran.join(" · ")}</div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit Periode" : "Tambah Periode"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Nama Periode</Label>
                            <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="mis. Semester Ganjil 2026/2027" />
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Mulai</Label>
                                <Input type="date" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} />
                            </div>
                            <div className="flex-1">
                                <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Selesai</Label>
                                <Input type="date" value={form.tanggal_selesai} onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">
                                Departemen/KSM Aktif (kosongkan semua = berlaku untuk semua prodi)
                            </Label>
                            <div className="mt-2 border rounded-lg p-3 max-h-52 overflow-y-auto space-y-1.5">
                                <label className="flex items-center gap-2 text-sm cursor-pointer font-semibold text-slate-700 pb-1.5 mb-1.5 border-b border-slate-200">
                                    <Checkbox
                                        checked={allProdiSelected ? true : someProdiSelected ? "indeterminate" : false}
                                        onCheckedChange={togglePilihSemua}
                                        data-testid="peer-period-pilih-semua"
                                    />
                                    Pilih Semua
                                </label>
                                {prodiList.map((p) => (
                                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <Checkbox checked={form.prodi_ids_aktif.includes(p.id)} onCheckedChange={() => toggleProdi(p.id)} />
                                        {p.nama_prodi}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
                        <Button onClick={submitPeriod} className="bg-[#00B9AD] hover:bg-[#60C0D0]">Simpan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus periode "{confirmDel?.nama}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Hanya bisa dihapus kalau belum ada penilaian yang masuk pada periode ini.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={delPeriod}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function PeriodPicker({ periods, value, onChange }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="w-72"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
            <SelectContent>
                {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}
